import type { RoomSummary } from '@brush/api-client';
import type { RundownSegment } from '@brush/ui';

/**
 * The gallery's running order, drawn from the room summary (`GET /api/rooms/{roomId}/summary`).
 *
 * The server makes every cut — what is live, what is scheduled from the room's own day on, every
 * finished event with no results, the two latest settled — at the room's gaming day (ADR 0014), and
 * runs each day in start-time order. This file only words it: the day labels, the detail line, and
 * the one action a segment may carry.
 *
 * Two constraints, both from CLAUDE.md, unchanged: a tournament's date is date-only, so segments are
 * labelled with a day and nothing invents an hour; and the only work the rundown can honestly flag is a
 * finished tournament with no results recorded.
 */

// Weekday, month and day — "Mon, Nov 2". A day of the month and a weekday alone ("2 Mon") named no month.
const DAY_LABEL = new Intl.DateTimeFormat(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
const SPAN_LABEL = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' });

/** "Today" is the room's day as the server reported it — never the browser's calendar. */
function label(date: string, today: string, formatter: Intl.DateTimeFormat): string {
  if (date === today) return 'Today';
  const parsed = new Date(`${date}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? date : formatter.format(parsed);
}

export function rundownSegments(summary: RoomSummary, onOpen: () => void): RundownSegment[] {
  return summary.rundown.map((r) => {
    const segment: RundownSegment = {
      id: r.id,
      when: label(r.date, summary.day, DAY_LABEL),
      name: r.name,
      detail: `$${r.buyIn.toLocaleString()} · ${r.entries.toLocaleString()} ${r.entries === 1 ? 'entry' : 'entries'}`,
      state: r.state === 'awaiting' || r.state === 'settled' ? 'past' : (r.state as RundownSegment['state']),
    };

    // Finished with nothing recorded is the work, so it is the one segment with an action — and only an
    // action turns a segment orange.
    return r.state === 'awaiting'
      ? { ...segment, detail: 'Finished · no results recorded', action: { label: 'Open', onSelect: onOpen } }
      : segment;
  });
}

/**
 * The heading over the rail. With a Rundown period set it spans the window the server resolved
 * (`rundownWindow`, UP21) — even over an empty rundown, since "nothing in the next 7 days" is an answer.
 * Under All upcoming (#131, known gap 42; refined at the walk, 2026-10-30) it says how far the schedule reaches —
 * "Through Nov 9" — and never a range: All upcoming has no end, so "Nov 2 – Nov 9" beside it was a contradiction, and
 * spanning every tournament the room holds headed a short rundown with months of history. The date is the latest live or
 * scheduled one in the rundown (the past it also shows is not the schedule; the rundown runs the past newest first, so
 * this is a maximum, not the last item). Empty when nothing is live or scheduled.
 */
export function rundownSpan(summary: RoomSummary): string {
  const window = summary.rundownWindow;
  if (window) {
    const from = label(window.from, summary.day, SPAN_LABEL);
    const to = label(window.to, summary.day, SPAN_LABEL);
    return from === to ? from : `${from} – ${to}`;
  }
  // ISO dates order as strings.
  const scheduled = summary.rundown.filter((r) => r.state !== 'awaiting' && r.state !== 'settled').map((r) => r.date).sort();
  if (scheduled.length === 0) return '';
  const last = scheduled[scheduled.length - 1];
  return last === summary.day ? 'Through today' : `Through ${label(last, summary.day, SPAN_LABEL)}`;
}
